import AuthService from '@/services/AuthService.js';
import CandidateService from '@/services/CandidateService.js';
import { ApplicationStatus } from '@/enum/application-status.js';
import { invitationStatusForResponse } from '@/enum/interview.js';

export default {
  // Initializes Keycloak once and records the resulting auth state.
  async initAuth({ commit }) {
    const isAuthenticated = await AuthService.init();

    commit('setAuthState', {
      initialized: true,
      isAuthenticated,
      candidateId: AuthService.candidateId
    });

    return isAuthenticated;
  },

  login({ rootState }, redirectUri) {
    AuthService.setLocale(rootState.currentLanguage);
    AuthService.login(redirectUri);
  },

  loginWith({ rootState }, { idpHint, redirectUri }) {
    AuthService.setLocale(rootState.currentLanguage);
    AuthService.loginWith(idpHint, redirectUri);
  },

  logout(context, redirectUri) {
    AuthService.logout(redirectUri);
  },

  async loadApplications({ commit }) {
    commit('setApplicationsLoading', true);
    commit('setLoadFailed', false);

    try {
      const response = await CandidateService.getApplications();
      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      const data = await response.json();
      commit('setApplications', data.applications || []);
      commit('setMustAcceptInvitations', data.mustAcceptInvitations || false);
    } catch (e) {
      commit('setLoadFailed', true);
    } finally {
      commit('setApplicationsLoading', false);
    }
  },

  // Accept, deny or request an alternative for an interview appointment. Reuses
  // the existing token-based appointment endpoint (application auth token +
  // per-interview token) via the application module, then reflects the new
  // invitation status locally.
  async respondToAppointment(
    { commit, dispatch },
    {
      applicationId,
      interviewId,
      authToken,
      interviewToken,
      responseType,
      comment
    }
  ) {
    try {
      const response = await dispatch(
        'applicationModule/updateAppointment',
        { applicationId, authToken, interviewToken, responseType, comment },
        { root: true }
      );

      if (!response || !response.ok) {
        return { success: false };
      }

      const result = await response.json();
      if (result.success) {
        commit('setInterviewStatus', {
          applicationId,
          interviewId,
          invitationStatusId: invitationStatusForResponse(responseType)
        });
      }

      return result;
    } catch (e) {
      return { success: false };
    }
  },

  async cancelApplication({ commit }, applicationId) {
    try {
      const response = await CandidateService.cancelApplication(applicationId);
      if (!response.ok) {
        return { success: false };
      }

      const result = await response.json();
      if (result.success) {
        // Reflect the new status locally without a full reload.
        commit('setApplicationStatus', {
          applicationId,
          statusId: ApplicationStatus.DeniedByCandidate
        });
      }

      return result;
    } catch (e) {
      return { success: false };
    }
  },

  async contactRecruiter(context, { applicationId, message }) {
    try {
      const response = await CandidateService.contactRecruiter(
        applicationId,
        message
      );
      if (!response.ok) {
        return { success: false };
      }

      return await response.json();
    } catch (e) {
      return { success: false };
    }
  },

  async loadProfile({ commit }) {
    commit('setProfileLoading', true);
    commit('setProfileError', false);

    try {
      const response = await CandidateService.getProfile();
      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      const profile = await response.json();
      commit('setProfile', profile);
    } catch (e) {
      commit('setProfileError', true);
    } finally {
      commit('setProfileLoading', false);
    }
  },

  async register(context, payload) {
    try {
      const response = await CandidateService.register(payload);
      if (!response.ok) {
        return { success: false, error: 'failed' };
      }

      return await response.json();
    } catch (e) {
      return { success: false, error: 'failed' };
    }
  },

  async confirmAccount(context, token) {
    try {
      const response = await CandidateService.confirm(token);
      if (!response.ok) {
        return { success: false, error: 'failed' };
      }

      return await response.json();
    } catch (e) {
      return { success: false, error: 'failed' };
    }
  },

  async reuseDocument(context, documentId) {
    const response = await CandidateService.reuseDocument(documentId);
    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}`);
    }

    return await response.json();
  },

  // Redirects to Keycloak's TOTP setup; control leaves the SPA until the user
  // returns to redirectUri.
  enableTwoFactor({ rootState }, redirectUri) {
    AuthService.setLocale(rootState.currentLanguage);
    AuthService.enableTotp(redirectUri);
  },

  // Redirects to Keycloak's change-password page; control leaves the SPA until
  // the user returns to redirectUri.
  changePassword({ rootState }, redirectUri) {
    AuthService.setLocale(rootState.currentLanguage);
    AuthService.changePassword(redirectUri);
  },

  async loadTwoFactorStatus() {
    try {
      const response = await CandidateService.getTwoFactorStatus();
      if (!response.ok) {
        return { enabled: false };
      }

      return await response.json();
    } catch (e) {
      return { enabled: false };
    }
  },

  async disableTwoFactor() {
    try {
      const response = await CandidateService.disableTwoFactor();
      if (!response.ok) {
        return { success: false };
      }

      return { success: true };
    } catch (e) {
      return { success: false };
    }
  },

  async updateDocuments({ commit }, payload) {
    try {
      const response = await CandidateService.updateDocuments(payload);
      if (!response.ok) {
        return { success: false };
      }

      const documents = await response.json();
      commit('setProfileDocuments', documents);

      return { success: true, documents };
    } catch (e) {
      return { success: false };
    }
  },

  async deleteProfile() {
    try {
      const response = await CandidateService.deleteProfile();
      if (!response.ok) {
        return { success: false };
      }

      return await response.json();
    } catch (e) {
      return { success: false };
    }
  },

  async saveProfile({ commit }, profile) {
    commit('setProfileSaving', true);
    commit('setProfileError', false);

    try {
      const response = await CandidateService.updateProfile(profile);
      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      const updated = await response.json();
      commit('setProfile', updated);
      return true;
    } catch (e) {
      commit('setProfileError', true);
      return false;
    } finally {
      commit('setProfileSaving', false);
    }
  }
};
